/**
 * Fills PCB Bank's "Full Review" questionnaire.
 *
 * The official PDF is flat — no AcroForm — so the answers are DRAWN on it:
 * an X on the blank after Yes or No, text along the answer lines. Where each
 * one lands comes from overlay.json (top-down page coordinates measured off
 * the official file); this module converts to PDF's bottom-up space and
 * writes with pdf-lib. The result needs no flattening: it never had fields.
 */

import { PDFDocument, StandardFonts, rgb } from "pdf-lib";
import type { PDFFont, PDFPage } from "pdf-lib";

import { PDF_FILE, allFields } from "./spec.mjs";
import overlayJson from "./overlay.mjs";

export interface FillContext {
  [key: string]: string | null | undefined;
}

export interface FillReport {
  text: number;
  boxes: number;
  problems: string[];
}

export interface FillOptions {
  pdfBytes?: ArrayBuffer | Uint8Array;
  pdfUrl?: string;
  /** Accepted for interface parity; a flat PDF has nothing to flatten. */
  flatten?: boolean;
}

interface Slot {
  page: number;
  x: number;
  y: number;
  w?: number;
}

const OVERLAY = overlayJson as unknown as Record<string, Slot> & { _page_heights: number[] };

function isYes(value: unknown): boolean {
  return /^(yes|y|true|on|1)$/i.test(String(value ?? "").trim());
}

function isNo(value: unknown): boolean {
  return /^(no|n|false|off|0)$/i.test(String(value ?? "").trim());
}

function formatDate(value: string | null | undefined): string {
  if (!value) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim());
  return m ? `${m[2]}/${m[3]}/${m[1]}` : String(value).trim();
}

export function defaultPdfUrl(): string {
  const base = (import.meta as any).env?.BASE_URL ?? "/";
  return `${base.endsWith("/") ? base : base + "/"}forms/${PDF_FILE}`;
}

function slotOf(id: string): Slot {
  const slot = OVERLAY[id];
  if (!slot || typeof slot.page !== "number") {
    throw new Error(`overlay.json has no slot "${id}"`);
  }
  return slot;
}

/** overlay.json measures top-down; pdf-lib draws bottom-up from the baseline. */
function baseline(slot: Slot, size: number): number {
  return OVERLAY._page_heights[slot.page] - slot.y - size * 0.35;
}

function drawText(pages: PDFPage[], font: PDFFont, id: string, value: string) {
  const slot = slotOf(id);
  let size = 9;
  if (slot.w) {
    while (size > 5 && font.widthOfTextAtSize(value, size) > slot.w - 4) size -= 0.5;
  }
  pages[slot.page].drawText(value, {
    x: slot.x,
    y: baseline(slot, size),
    size,
    font,
    color: rgb(0.05, 0.05, 0.25),
  });
}

function drawMark(pages: PDFPage[], font: PDFFont, id: string) {
  const slot = slotOf(id);
  pages[slot.page].drawText("X", {
    x: slot.x - 3,
    y: baseline(slot, 10),
    size: 10,
    font,
    color: rgb(0.05, 0.05, 0.25),
  });
}

export async function fillPcb(
  answers: Record<string, string | null | undefined>,
  _context: FillContext = {},
  options: FillOptions = {},
): Promise<{ bytes: Uint8Array; report: FillReport }> {
  let pdfBytes = options.pdfBytes;
  if (!pdfBytes) {
    const url = options.pdfUrl ?? defaultPdfUrl();
    const response = await fetch(url);
    if (!response.ok) {
      throw new Error(`Could not load the blank form from ${url} (${response.status})`);
    }
    pdfBytes = await response.arrayBuffer();
  }

  const document = await PDFDocument.load(pdfBytes);
  const pages = document.getPages();
  const font = await document.embedFont(StandardFonts.Helvetica);
  const bold = await document.embedFont(StandardFonts.HelveticaBold);
  const report: FillReport = { text: 0, boxes: 0, problems: [] };

  for (const field of allFields() as any[]) {
    const raw = answers[field.name];
    if (raw === null || raw === undefined || String(raw).trim() === "") continue;
    const value = field.type === "date" ? formatDate(String(raw)) : String(raw).trim();
    const pdf = field.pdf;
    if (!pdf) continue;

    try {
      if (pdf.mark) {
        let side: "yes" | "no" | null = isYes(value) ? "yes" : isNo(value) ? "no" : null;
        if (side === null) continue;
        if (pdf.invert) side = side === "yes" ? "no" : "yes";
        drawMark(pages, bold, `${pdf.mark}.${side}`);
        report.boxes++;
      } else if (pdf.markByOption) {
        const slot = pdf.markByOption[value];
        if (!slot) continue;
        drawMark(pages, bold, slot);
        report.boxes++;
      } else if (pdf.write) {
        const printed = pdf.map ? (pdf.map[value] ?? value) : value;
        const targets = Array.isArray(pdf.write) ? pdf.write : [pdf.write];
        for (const target of targets) {
          drawText(pages, font, target, printed);
          report.text++;
        }
      }
    } catch (error) {
      report.problems.push(`${field.name}: ${(error as Error).message}`);
    }
  }

  const bytes = await document.save();
  return { bytes, report };
}
